import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ProjectDetailModal from "../../components/ProjectDetailModal";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";

interface Project {
  _id: string;
  title: string;
  description: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  age: string;
  category: string;
  platform: string;
  bannerImage: string;
  isActive: boolean;
}

const AllProjects: React.FC = () => {
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [selectedPlatform, setSelectedPlatform] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch projects from API
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3030'}/highcode/projects`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch projects');
        }
        
        const data = await response.json();
        setProjects(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load projects');
        console.error('Error fetching projects:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Array of available banner images
  const bannerImages = [
    '/images/projects/astropi-ndvi-banner.png',
    '/images/projects/blockscad-bug-banner.png',
    '/images/projects/butterfly-banner.png',
    '/images/projects/cats-banner.png',
    '/images/projects/cats-vs-dogs-banner.png',
    '/images/projects/cd-sebento-htmlcss-1-banner.png',
    '/images/projects/cd-sebento-htmlcss-2-banner.png',
    '/images/projects/cd-sebento-htmlcss-3-banner.png',
    '/images/projects/count-creatures-banner.png',
    '/images/projects/cress-egg-heads-banner.png',
    '/images/projects/decision-tree-banner.png',
    '/images/projects/grow-dragonfly-banner.png',
    '/images/projects/grow-tree-introduction.png',
    '/images/projects/infrared-bird-box-banner.png',
    '/images/projects/led-firefly-banner.png',
    '/images/projects/nature-rover-banner.png',
    '/images/projects/popular-pets-banner.png',
    '/images/projects/sense-hat-data-logger-banner.png',
    '/images/projects/serene-scene-banner.png',
    '/images/projects/sprite-needs-you-banner.png',
    '/images/projects/sunrise-banner.png',
    '/images/projects/swarms-schools-flocks-banner.png',
    '/images/projects/tree-life-simulator-banner.png',
    '/images/projects/turtle-race-banner.png',
    '/images/projects/turtle-snowflakes-banner.png',
    '/images/projects/turtle-tracker-banner.png',
    '/images/projects/welcome-antarctica-banner.png',
    '/images/projects/world-environment-day-banner.png'
  ];

  // Function to get a banner image based on project index
  const getBannerImage = (index: number) => {
    return bannerImages[index % bannerImages.length];
  };


  // Show loading spinner while fetching
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Show error if projects failed to load
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Failed to Load Projects</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const filteredProjects = projects.filter(project => {
    const matchesFilter = selectedFilter === "All" || project.difficulty === selectedFilter;
    const matchesPlatform = selectedPlatform === "All" || project.platform === selectedPlatform;
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.platform.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesPlatform && matchesSearch;
  });

  const handleViewProject = (project: any) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const categories = ["All", ...Array.from(new Set(projects.map(p => p.category)))];
  const difficulties = ["All", "Easy", "Medium", "Hard"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-white">
        <div className="container mx-auto px-6 py-12">
          <div className="text-center mt-16">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-800 mb-4">
              🎨 All Projects 🎨
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              Explore our complete collection of {projects.length}+ STEM projects for all skill levels!
            </p>
            
            {/* Search Bar */}
            <div className="max-w-md mx-auto mb-6">
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Platform Filter Tabs */}
            <div className="flex flex-wrap justify-center gap-4 mb-4">
              <div className="bg-gray-100 rounded-full p-2">
                <button
                  onClick={() => setSelectedPlatform("All")}
                  className={`px-4 py-2 rounded-full font-semibold mr-2 transition-all duration-300 ${
                    selectedPlatform === "All" 
                      ? "bg-purple-100 text-purple-800" 
                      : "text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  All Platforms
                </button>
                <button
                  onClick={() => setSelectedPlatform("Arduino")}
                  className={`px-4 py-2 rounded-full font-semibold mr-2 transition-all duration-300 ${
                    selectedPlatform === "Arduino" 
                      ? "bg-blue-100 text-blue-800" 
                      : "text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  Arduino
                </button>
                <button
                  onClick={() => setSelectedPlatform("Raspberry Pi")}
                  className={`px-4 py-2 rounded-full font-semibold mr-2 transition-all duration-300 ${
                    selectedPlatform === "Raspberry Pi" 
                      ? "bg-red-100 text-red-800" 
                      : "text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  Raspberry Pi
                </button>
                <button
                  onClick={() => setSelectedPlatform("ESP32")}
                  className={`px-4 py-2 rounded-full font-semibold mr-2 transition-all duration-300 ${
                    selectedPlatform === "ESP32" 
                      ? "bg-green-100 text-green-800" 
                      : "text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  ESP32
                </button>
                <button
                  onClick={() => setSelectedPlatform("ESP8266")}
                  className={`px-4 py-2 rounded-full font-semibold transition-all duration-300 ${
                    selectedPlatform === "ESP8266" 
                      ? "bg-orange-100 text-orange-800" 
                      : "text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  ESP8266
                </button>
              </div>
            </div>

            {/* Difficulty Filter Tabs */}
            <div className="flex flex-wrap justify-center gap-4 mb-6">
              <div className="bg-gray-100 rounded-full p-2">
                {difficulties.map((difficulty) => (
                  <button
                    key={difficulty}
                    onClick={() => setSelectedFilter(difficulty)}
                    className={`px-4 py-2 rounded-full font-semibold mr-2 transition-all duration-300 ${
                      selectedFilter === difficulty
                        ? difficulty === "Easy" ? "bg-green-100 text-green-800" :
                          difficulty === "Medium" ? "bg-yellow-100 text-yellow-800" :
                          difficulty === "Hard" ? "bg-red-100 text-red-800" :
                          "bg-blue-100 text-blue-800"
                        : "text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {difficulty}
                  </button>
                ))}
              </div>
            </div>

            <p className="text-sm text-gray-500">
              Showing {filteredProjects.length} of {projects.length} projects
            </p>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProjects.map((project, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden">
              {/* Top Graphical Section */}
              <div className="h-48 relative overflow-hidden">
                  <img 
                    src={getBannerImage(index)} 
                    alt={`${project.title} project illustration`}
                    className="w-full h-full object-cover"
                  />
              </div>
              
              {/* Bottom Text Section */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-black" style={{fontFamily: 'Lexend, sans-serif'}}>
                    ☐☐☐ {project.platform} - Level {project.difficulty}
                  </span>
                  <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                    project.platform === 'Arduino' ? 'bg-blue-100 text-blue-800' :
                    project.platform === 'Raspberry Pi' ? 'bg-red-100 text-red-800' :
                    project.platform === 'ESP32' ? 'bg-green-100 text-green-800' :
                    'bg-orange-100 text-orange-800'
                  }`}>
                    {project.platform}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-black mb-2 underline" style={{fontFamily: 'Lexend, sans-serif'}}>
                  {project.title}
                </h3>
                <p className="text-sm text-black mb-4" style={{fontFamily: 'Lexend, sans-serif'}}>
                  {project.description}
                </p>
                 <Link 
                   to={`/project/${project._id}`}
                   className="flex items-center space-x-2 text-sm font-bold text-black hover:text-gray-600 transition-colors" 
                   style={{fontFamily: 'Lexend, sans-serif'}}
                 >
                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                   </svg>
                   <span>View Project</span>
                 </Link>
              </div>
            </div>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">No projects found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>

      {/* Back to High Code */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 py-12">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Start Building?
          </h2>
          <p className="text-white/90 mb-6">
            Get your starter pack and begin your STEM journey today!
          </p>
          <Link
            to="/high-code"
            className="inline-block bg-white text-purple-600 font-bold py-3 px-8 rounded-full text-lg transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            ← Back to High Code
          </Link>
        </div>
      </div>

      {/* Project Detail Modal */}
      {selectedProject && (
        <ProjectDetailModal
          project={selectedProject}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default AllProjects;
